// Fireworks effect component for login page
// Based on Caleb Miller's fireworks - https://cmiller.tech/

import { Box } from "@mui/material";
import { memo, useEffect, useRef, useCallback } from "react";

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

const GRAVITY = 0.9;
const COLOR = {
  Red: '#ff0043',
  Green: '#14fc56',
  Blue: '#1e7fff',
  Purple: '#e60aff',
  Gold: '#ffbf36',
  White: '#ffffff'
};
const INVISIBLE = '_INVISIBLE_';
const PI_2 = Math.PI * 2;
const PI_HALF = Math.PI * 0.5;

const COLOR_CODES = Object.values(COLOR);
const COLOR_CODES_W_INVIS = [...COLOR_CODES, INVISIBLE];

const COLOR_TUPLES: Record<string, { r: number; g: number; b: number }> = {};
COLOR_CODES.forEach(hex => {
  COLOR_TUPLES[hex] = {
    r: parseInt(hex.substr(1, 2), 16),
    g: parseInt(hex.substr(3, 2), 16),
    b: parseInt(hex.substr(5, 2), 16),
  };
});

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function randomColorSimple() {
  return COLOR_CODES[Math.random() * COLOR_CODES.length | 0];
}

let lastColor: string;
function randomColor(options?: { notSame?: boolean; notColor?: string; limitWhite?: boolean }) {
  const notSame = options?.notSame;
  const notColor = options?.notColor;
  const limitWhite = options?.limitWhite;
  let color = randomColorSimple();

  if (limitWhite && color === COLOR.White && Math.random() < 0.6) {
    color = randomColorSimple();
  }

  if (notSame) {
    while (color === lastColor) {
      color = randomColorSimple();
    }
  } else if (notColor) {
    while (color === notColor) {
      color = randomColorSimple();
    }
  }

  lastColor = color;
  return color;
}

function whiteOrGold() {
  return Math.random() < 0.5 ? COLOR.Gold : COLOR.White;
}

function makePistilColor(shellColor: string) {
  return (shellColor === COLOR.White || shellColor === COLOR.Gold)
    ? randomColor({ notColor: shellColor })
    : whiteOrGold();
}

// ============================================================================
// STAR CLASS
// ============================================================================

interface StarData {
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  speedX: number;
  speedY: number;
  life: number;
  fullLife: number;
  color: string;
  secondColor?: string;
  colorChanged: boolean;
  transitionTime: number;
  spinAngle: number;
  spinRadius: number;
  spinSpeed: number;
  sparkFreq: number;
  sparkSpeed: number;
  sparkTimer: number;
  sparkColor: string;
  sparkLife: number;
  sparkLifeVariation: number;
  strobe: boolean;
  strobeFreq: number;
  strobeColor?: string;
  visible: boolean;
  heavy: boolean;
  updateFrame: number;
}

const starPool: StarData[] = [];
const activeStars: Record<string, StarData[]> = {};
COLOR_CODES_W_INVIS.forEach(color => {
  activeStars[color] = [];
});

function createStar(): StarData {
  return {
    x: 0, y: 0, prevX: 0, prevY: 0,
    speedX: 0, speedY: 0,
    life: 0, fullLife: 0,
    color: COLOR.White,
    secondColor: undefined,
    colorChanged: false,
    transitionTime: 0,
    spinAngle: 0, spinRadius: 0, spinSpeed: 0,
    sparkFreq: 0, sparkSpeed: 0, sparkTimer: 0,
    sparkColor: COLOR.Gold, sparkLife: 0, sparkLifeVariation: 0,
    strobe: false, strobeFreq: 0, strobeColor: undefined,
    visible: true, heavy: false, updateFrame: 0
  };
}

function getStarInstance(): StarData {
  return starPool.pop() || createStar();
}

function returnStarInstance(star: StarData) {
  starPool.push(star);
}

function addStar(
  x: number, y: number, color: string, angle: number, speed: number,
  life: number, options?: Partial<StarData>
) {
  const star = getStarInstance();
  star.x = star.prevX = x;
  star.y = star.prevY = y;
  star.color = color;
  star.speedX = Math.sin(angle) * speed;
  star.speedY = Math.cos(angle) * speed;
  star.life = star.fullLife = life;
  star.colorChanged = false;
  star.visible = true;
  star.updateFrame = 0;

  // Reset optional properties
  star.secondColor = options?.secondColor;
  star.transitionTime = options?.transitionTime || 0;
  star.spinAngle = options?.spinAngle || 0;
  star.spinRadius = options?.spinRadius || 0;
  star.spinSpeed = options?.spinSpeed || 0;
  star.sparkFreq = options?.sparkFreq || 0;
  star.sparkSpeed = options?.sparkSpeed || 0;
  star.sparkTimer = 0;
  star.sparkColor = options?.sparkColor || COLOR.Gold;
  star.sparkLife = options?.sparkLife || 750;
  star.sparkLifeVariation = options?.sparkLifeVariation || 0.25;
  star.strobe = options?.strobe || false;
  star.strobeFreq = options?.strobeFreq || 0;
  star.strobeColor = options?.strobeColor;
  star.heavy = options?.heavy || false;

  activeStars[color].push(star);
  return star;
}

// ============================================================================
// SPARK CLASS
// ============================================================================

interface SparkData {
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  speedX: number;
  speedY: number;
  life: number;
  fullLife: number;
  color: string;
  updateFrame: number;
}

const sparkPool: SparkData[] = [];
const activeSparks: SparkData[] = [];

function createSpark(): SparkData {
  return {
    x: 0, y: 0, prevX: 0, prevY: 0,
    speedX: 0, speedY: 0,
    life: 0, fullLife: 0,
    color: COLOR.White,
    updateFrame: 0
  };
}

function getSparkInstance(): SparkData {
  return sparkPool.pop() || createSpark();
}

function returnSparkInstance(spark: SparkData) {
  sparkPool.push(spark);
}

function addSpark(x: number, y: number, color: string, angle: number, speed: number, life: number) {
  const spark = getSparkInstance();
  spark.x = spark.prevX = x;
  spark.y = spark.prevY = y;
  spark.color = color;
  spark.speedX = Math.sin(angle) * speed;
  spark.speedY = Math.cos(angle) * speed;
  spark.life = spark.fullLife = life;
  spark.updateFrame = 0;
  activeSparks.push(spark);
}

// ============================================================================
// SHELL CONFIGURATIONS
// ============================================================================

function crysanthemumShell(size = 1) {
  const glitter = Math.random() < 0.25;
  const singleColor = Math.random() < 0.72;
  const color = singleColor ? randomColor({ limitWhite: true }) : [randomColor(), randomColor({ notSame: true })];
  const pistil = singleColor && Math.random() < 0.42;
  const pistilColor = pistil ? makePistilColor(color as string) : undefined;
  const secondColor = singleColor && (Math.random() < 0.2 || color === COLOR.White)
    ? pistilColor || randomColor({ notColor: color as string, limitWhite: true })
    : null;
  const streamers = !pistil && color !== COLOR.White && Math.random() < 0.42;
  const starDensity = glitter ? 1.1 : 1.25;

  return {
    shellSize: size,
    spreadSize: 300 + size * 100,
    starLife: 900 + size * 200,
    starDensity,
    color,
    secondColor,
    glitter: glitter ? 'light' : '',
    glitterColor: whiteOrGold(),
    pistil,
    pistilColor,
    streamers
  };
}

function randomShell(size: number) {
  return crysanthemumShell(size);
}

// ============================================================================
// SHELL CLASS
// ============================================================================

interface ShellInstance {
  x: number;
  y: number;
  speedX: number;
  speedY: number;
  life: number;
  color: string;
  secondColor?: string | null;
  spreadSize: number;
  starLife: number;
  starDensity: number;
  glitter?: string;
  glitterColor?: string;
  pistil?: boolean;
  pistilColor?: string;
  streamers?: boolean;
  comet?: StarData;
}

const activeShells: ShellInstance[] = [];

function launchShell(stageW: number, stageH: number) {
  const size = 2 + Math.random() * 2;
  const config = randomShell(size);

  const launchX = 0.2 + Math.random() * 0.6;
  // Altura más arriba: explotan entre 10% y 45% desde el tope de la pantalla
  const launchHeight = 0.1 + Math.random() * 0.35;

  const shell: ShellInstance = {
    x: launchX * stageW,
    y: stageH,
    speedX: 0,
    speedY: 0,
    life: 0,
    color: Array.isArray(config.color) ? config.color[0] : config.color,
    secondColor: config.secondColor,
    spreadSize: config.spreadSize,
    starLife: config.starLife,
    starDensity: config.starDensity,
    glitter: config.glitter,
    glitterColor: config.glitterColor,
    pistil: config.pistil,
    pistilColor: config.pistilColor,
    streamers: config.streamers
  };

  const targetY = launchHeight * stageH;
  const launchVelocity = Math.pow(stageH - targetY, 0.72) * 0.04;

  shell.speedY = -launchVelocity;
  shell.speedX = (Math.random() - 0.5) * 2;

  // Add comet trail
  shell.comet = addStar(
    shell.x, shell.y,
    randomColor({ limitWhite: true }),
    PI_HALF, 0,
    launchVelocity * 26,
    {
      sparkFreq: 20,
      sparkSpeed: 0.8,
      sparkLife: 400,
      sparkColor: COLOR.Gold,
      heavy: true
    }
  );

  activeShells.push(shell);
}

function explodeShell(shell: ShellInstance) {
  const { x, y, color, spreadSize, starLife, starDensity, glitter, glitterColor, pistil, pistilColor, streamers, secondColor } = shell;

  const starCount = Math.floor(spreadSize * starDensity * 0.5);

  for (let i = 0; i < starCount; i++) {
    const angle = Math.random() * PI_2;
    const speed = (Math.random() * 0.6 + 0.4) * spreadSize * 0.015;

    const starOptions: Partial<StarData> = {};

    if (secondColor) {
      starOptions.secondColor = secondColor;
      starOptions.transitionTime = starLife * 0.4;
    }

    if (glitter) {
      starOptions.sparkFreq = glitter === 'light' ? 300 : 100;
      starOptions.sparkSpeed = 0.5;
      starOptions.sparkLife = 500;
      starOptions.sparkColor = glitterColor || COLOR.Gold;
    }

    if (streamers) {
      starOptions.sparkFreq = 80;
      starOptions.sparkSpeed = 1.2;
      starOptions.sparkLife = 800;
      starOptions.sparkColor = color;
    }

    addStar(x, y, color, angle, speed, starLife, starOptions);
  }

  // Pistil
  if (pistil && pistilColor) {
    const pistilCount = Math.floor(starCount * 0.2);
    for (let i = 0; i < pistilCount; i++) {
      const angle = Math.random() * PI_2;
      const speed = (Math.random() * 0.3 + 0.2) * spreadSize * 0.01;
      addStar(x, y, pistilColor, angle, speed, starLife * 0.8);
    }
  }
}

// ============================================================================
// FIREWORKS COMPONENT
// ============================================================================

const Fireworks = memo(function Fireworks() {
  const trailsCanvasRef = useRef<HTMLCanvasElement>(null);
  const mainCanvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const autoLaunchTimeRef = useRef<number>(0);
  const currentFrameRef = useRef<number>(0);
  const stageRef = useRef({ w: 0, h: 0 });

  const clearStars = useCallback(() => {
    COLOR_CODES_W_INVIS.forEach(color => {
      activeStars[color].length = 0;
    });
    activeSparks.length = 0;
    activeShells.length = 0;
  }, []);

  const update = useCallback((timeStep: number, speed: number) => {
    currentFrameRef.current++;
    const currentFrame = currentFrameRef.current;

    const starDrag = 1 - (1 - 0.985) * speed;
    const sparkDrag = 1 - (1 - 0.95) * speed;
    const gAcc = timeStep / 1000 * GRAVITY * speed;

    // Update shells
    for (let i = activeShells.length - 1; i >= 0; i--) {
      const shell = activeShells[i];
      shell.y += shell.speedY * speed;
      shell.x += shell.speedX * speed;
      shell.speedY += gAcc;

      if (shell.comet) {
        shell.comet.x = shell.x;
        shell.comet.y = shell.y;
        shell.comet.prevX = shell.x;
        shell.comet.prevY = shell.y;
      }

      // Explode when velocity reverses (apex)
      if (shell.speedY >= 0) {
        explodeShell(shell);
        if (shell.comet) {
          const cometColor = shell.comet.color;
          const idx = activeStars[cometColor].indexOf(shell.comet);
          if (idx > -1) {
            activeStars[cometColor].splice(idx, 1);
            returnStarInstance(shell.comet);
          }
        }
        activeShells.splice(i, 1);
      }
    }

    // Update stars
    COLOR_CODES_W_INVIS.forEach(color => {
      const stars = activeStars[color];
      for (let i = stars.length - 1; i >= 0; i--) {
        const star = stars[i];
        if (star.updateFrame === currentFrame) continue;
        star.updateFrame = currentFrame;

        star.life -= timeStep;
        if (star.life <= 0) {
          stars.splice(i, 1);
          returnStarInstance(star);
        } else {
          const burnRate = Math.pow(star.life / star.fullLife, 0.5);
          const burnRateInverse = 1 - burnRate;

          star.prevX = star.x;
          star.prevY = star.y;
          star.x += star.speedX * speed;
          star.y += star.speedY * speed;

          if (!star.heavy) {
            star.speedX *= starDrag;
            star.speedY *= starDrag;
          }
          star.speedY += gAcc;

          // Sparks
          if (star.sparkFreq) {
            star.sparkTimer -= timeStep;
            while (star.sparkTimer < 0) {
              star.sparkTimer += star.sparkFreq * 0.75 + star.sparkFreq * burnRateInverse * 4;
              addSpark(
                star.x, star.y, star.sparkColor,
                Math.random() * PI_2,
                Math.random() * star.sparkSpeed * burnRate,
                star.sparkLife * 0.8 + Math.random() * star.sparkLifeVariation * star.sparkLife
              );
            }
          }

          // Color transition
          if (star.transitionTime && star.life < star.transitionTime && star.secondColor && !star.colorChanged) {
            star.colorChanged = true;
            star.color = star.secondColor;
            stars.splice(i, 1);
            activeStars[star.secondColor].push(star);
            if (star.secondColor === INVISIBLE) {
              star.sparkFreq = 0;
            }
          }

          // Strobe
          if (star.strobe) {
            star.visible = Math.floor(star.life / star.strobeFreq) % 3 === 0;
          }
        }
      }
    });

    // Update sparks
    for (let i = activeSparks.length - 1; i >= 0; i--) {
      const spark = activeSparks[i];
      spark.life -= timeStep;
      if (spark.life <= 0) {
        activeSparks.splice(i, 1);
        returnSparkInstance(spark);
      } else {
        spark.prevX = spark.x;
        spark.prevY = spark.y;
        spark.x += spark.speedX * speed;
        spark.y += spark.speedY * speed;
        spark.speedX *= sparkDrag;
        spark.speedY *= sparkDrag;
        spark.speedY += gAcc;
      }
    }
  }, []);

  const render = useCallback((trailsCtx: CanvasRenderingContext2D, mainCtx: CanvasRenderingContext2D, scaleFactor: number) => {
    const stageW = stageRef.current.w;
    const stageH = stageRef.current.h;

    // Clear with fade effect for trails
    trailsCtx.globalCompositeOperation = 'destination-out';
    trailsCtx.fillStyle = 'rgba(0, 0, 0, 0.2)';
    trailsCtx.fillRect(0, 0, stageW * scaleFactor, stageH * scaleFactor);
    trailsCtx.globalCompositeOperation = 'lighter';

    // Clear main canvas
    mainCtx.clearRect(0, 0, stageW * scaleFactor, stageH * scaleFactor);

    // Draw stars
    COLOR_CODES.forEach(color => {
      const stars = activeStars[color];
      if (stars.length === 0) return;

      const tuple = COLOR_TUPLES[color];
      trailsCtx.strokeStyle = `rgba(${tuple.r}, ${tuple.g}, ${tuple.b}, 0.6)`;
      trailsCtx.lineWidth = 1.5 * scaleFactor;
      trailsCtx.beginPath();

      stars.forEach(star => {
        if (!star.visible) return;
        trailsCtx.moveTo(star.prevX * scaleFactor, star.prevY * scaleFactor);
        trailsCtx.lineTo(star.x * scaleFactor, star.y * scaleFactor);
      });

      trailsCtx.stroke();

      // Draw star heads
      mainCtx.fillStyle = color;
      stars.forEach(star => {
        if (!star.visible) return;
        const size = 2 * scaleFactor;
        mainCtx.beginPath();
        mainCtx.arc(star.x * scaleFactor, star.y * scaleFactor, size, 0, PI_2);
        mainCtx.fill();
      });
    });

    // Draw sparks
    if (activeSparks.length > 0) {
      mainCtx.strokeStyle = 'rgba(255, 200, 100, 0.3)';
      mainCtx.lineWidth = 0.75 * scaleFactor;
      mainCtx.beginPath();

      activeSparks.forEach(spark => {
        mainCtx.moveTo(spark.prevX * scaleFactor, spark.prevY * scaleFactor);
        mainCtx.lineTo(spark.x * scaleFactor, spark.y * scaleFactor);
      });

      mainCtx.stroke();
    }
  }, []);

  useEffect(() => {
    const trailsCanvas = trailsCanvasRef.current;
    const mainCanvas = mainCanvasRef.current;
    if (!trailsCanvas || !mainCanvas) return;

    const trailsCtx = trailsCanvas.getContext('2d');
    const mainCtx = mainCanvas.getContext('2d');
    if (!trailsCtx || !mainCtx) return;

    const scaleFactor = window.devicePixelRatio || 1;

    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;

      stageRef.current.w = w;
      stageRef.current.h = h;

      trailsCanvas.width = w * scaleFactor;
      trailsCanvas.height = h * scaleFactor;
      trailsCanvas.style.width = w + 'px';
      trailsCanvas.style.height = h + 'px';

      mainCanvas.width = w * scaleFactor;
      mainCanvas.height = h * scaleFactor;
      mainCanvas.style.width = w + 'px';
      mainCanvas.style.height = h + 'px';
    };

    resize();
    window.addEventListener('resize', resize);

    // Animation loop
    const animate = (timestamp: number) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const delta = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      const timeStep = Math.min(delta, 33.33); // Cap at ~30fps minimum
      const speed = timeStep / 16.67;

      // Auto launch
      autoLaunchTimeRef.current -= timeStep;
      if (autoLaunchTimeRef.current <= 0) {
        launchShell(stageRef.current.w, stageRef.current.h);
        autoLaunchTimeRef.current = 800 + Math.random() * 1200;
      }

      update(timeStep, speed);
      render(trailsCtx, mainCtx, scaleFactor);

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Initial launch
    setTimeout(() => {
      launchShell(stageRef.current.w, stageRef.current.h);
    }, 500);

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameRef.current);
      clearStars();
    };
  }, [update, render, clearStars]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        backgroundColor: '#000',
        pointerEvents: 'none',
      }}
    >
      <canvas
        ref={trailsCanvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          mixBlendMode: 'lighten',
        }}
      />
      <canvas
        ref={mainCanvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          mixBlendMode: 'lighten',
        }}
      />
    </Box>
  );
});

export default Fireworks;
